/**
 * "Why is this for me?" on the posts people interact with most: the AI reads the post and your own profile and
 * says whether and why it matters to you, naming what matched. Paid with your AI credits; asked once, the
 * answer stays (and shows again) until the post is edited or your profile changes.
 * Backend: POST /interact/posts/<id>/why-for-me/ (interact/post/relevance.py).
 */
import React, { useState } from 'react';
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { AutoAwesome as SparkIcon, Close as CloseIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { axiosService } from 'shared/helpers/axios';
import { apiErrorMessage } from 'shared/helpers/apiError';
import { useSnackbar } from 'shared/contexts/SnackbarContext';
import PaymentDialog from 'components/common/billing/PaymentDialog';

const VERDICTS = {
  relevant: { label: 'Relevant to you', color: 'success' },
  partly: { label: 'Partly relevant', color: 'warning' },
  not: { label: 'Probably not for you', color: 'default' },
};

/** The button in a post's actions row. STATE: [answer, setAnswer] shared with the post. */
export function WhyForMeButton({ post, state }) {
  const [answer, setAnswer] = state;
  const [busy, setBusy] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const { showSnackbar = () => {} } = useSnackbar() || {};

  const ask = async () => {
    if (answer) return;
    setBusy(true);
    try {
      const { data } = await axiosService.post(`/interact/posts/${post.id}/why-for-me/`);
      setAnswer({ verdict: data.verdict, summary: data.summary, matched: data.matched || [], credits_used: data.credits_used });
    } catch (err) {
      if (err.response?.status === 402) setPaymentOpen(true);
      else showSnackbar(apiErrorMessage(err, 'Could not answer just now.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title={answer ? '' : 'The AI reads this post and your profile. Uses your AI credits (usually 1); the answer stays here.'}>
        <span>
          <Button size="small" startIcon={busy ? <CircularProgress size={14} /> : <SparkIcon />} onClick={ask} disabled={busy}
            sx={{ textTransform: 'none', color: answer ? 'primary.main' : 'text.secondary', '&:hover': { color: 'primary.main' } }}>
            Why is this for me?
          </Button>
        </span>
      </Tooltip>
      <PaymentDialog
        open={paymentOpen}
        onClose={() => setPaymentOpen(false)}
        productName="AI Credits"
        productDescription="Credits cover AI token costs. “Why is this for me?” usually uses 1 credit."
        unitLabel="credits"
        costPerOperation={1}
        operationLabel="answer"
        currentBalance={0}
        onSuccess={() => { setPaymentOpen(false); ask(); }}
      />
    </>
  );
}

/** The answer, under the post. */
export default function WhyForMe({ answer, onClose }) {
  const v = VERDICTS[answer.verdict] || VERDICTS.partly;
  return (
    <Box sx={(t) => ({
      mt: 1.5, mr: { xs: 0, sm: 2 }, p: 1.5, pr: 4, position: 'relative', borderRadius: 1.5,
      bgcolor: alpha(t.palette.primary.main, 0.04), borderLeft: '3px solid',
      borderColor: v.color === 'default' ? 'divider' : `${v.color}.main`,
    })}>
      <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
        <SparkIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        <Chip size="small" label={v.label} color={v.color} variant={v.color === 'default' ? 'outlined' : 'filled'}
          sx={{ height: 20, fontSize: '0.72rem' }} />
        <Typography variant="caption" color="text.secondary">from your profile, by AI</Typography>
      </Box>
      <Typography variant="body2">{answer.summary}</Typography>
      {answer.matched?.length > 0 && (
        <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.75}>
          {answer.matched.map((m) => <Chip key={m} size="small" variant="outlined" label={m} sx={{ height: 20, fontSize: '0.7rem' }} />)}
        </Box>
      )}
      {answer.credits_used > 0 && (
        <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
          {answer.credits_used} AI credit{answer.credits_used === 1 ? '' : 's'} used
        </Typography>
      )}
      <IconButton size="small" aria-label="Hide the answer" onClick={onClose} sx={{ position: 'absolute', top: 4, right: 4 }}>
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
}
